import { Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import DemoExitCTA from '@/components/DemoExitCTA';
import ConsentBanner from '@/components/ConsentBanner';

// Lazy-loaded secondary pages — not needed for initial landing page render
const Blog = lazy(() => import('@/pages/Blog'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const CaseStudies = lazy(() => import('@/pages/CaseStudies'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const AuditPage = lazy(() => import('@/pages/AuditPage'));
const WebsiteRedesign = lazy(() => import('@/pages/WebsiteRedesign'));
const NewEraOfSearch = lazy(() => import('@/pages/NewEraOfSearch'));
const WorkStudy = lazy(() => import('@/pages/WorkStudy'));
const Work = lazy(() => import('@/pages/Work'));
const ClientExperience = lazy(() => import('@/pages/ClientExperience'));
const VerticalLanding = lazy(() => import('@/pages/VerticalLanding'));
const TheExperienceGap = lazy(() => import('@/pages/TheExperienceGap'));
const PrivateClientExperienceLayer = lazy(() => import('@/pages/PrivateClientExperienceLayer'));
const AIBookingDeskPrototype = lazy(() => import('@/pages/AIBookingDeskPrototype'));
const WorldsWeDesign = lazy(() => import('@/pages/WorldsWeDesign'));
const Charter = lazy(() => import('@/pages/Charter'));
const Sector = lazy(() => import('@/pages/Sector'));
const YachtCharterWorld = lazy(() => import('@/pages/worlds/YachtCharter'));
const PrivateResidencesWorld = lazy(() => import('@/pages/worlds/PrivateResidences'));
const TravelExpeditionsWorld = lazy(() => import('@/pages/worlds/TravelExpeditions'));

const LuxuryTravelConciergePortals = lazy(() => import('@/pages/blog/LuxuryTravelConciergePortals'));
const PrivateOfficesForPremiumBrands = lazy(() => import('@/pages/blog/PrivateOfficesForPremiumBrands'));
const ValueArticle = lazy(() => import('@/pages/blog/ValueArticle'));
const DemoBHRN = lazy(() => import('@/pages/DemoBHRN'));
const Preview = lazy(() => import('@/pages/Preview'));
const SiennaPrivateRoom = lazy(() => import('@/pages/SiennaPrivateRoom'));
const TJBPrivateOffice = lazy(() => import('@/pages/TJBPrivateOffice'));
const VoguePrivateOffice = lazy(() => import('@/pages/VoguePrivateOffice'));
const BrowFactorDemo = lazy(() => import('@/pages/BrowFactorDemo'));
const KarmaPrivateSuite = lazy(() => import('@/pages/KarmaPrivateSuite'));
const MadisonHousePrivate = lazy(() => import('@/pages/MadisonHousePrivate'));
const CitiTravelDMC = lazy(() => import('@/pages/CitiTravelDMC'));
const HedonistExperience = lazy(() => import('@/pages/HedonistExperience'));
const PlanJapanPrivateOffice = lazy(() => import('@/pages/PlanJapanPrivateOffice'));
const CosmeticCoursesDemo = lazy(() => import('@/pages/CosmeticCoursesDemo'));
const GinaConnorTravelDemo = lazy(() => import('@/pages/GinaConnorTravelDemo'));
const BenditaDecoPrivate = lazy(() => import('@/pages/BenditaDecoPrivate'));
const YugawaraFukiyaDemo = lazy(() => import('@/pages/YugawaraFukiyaDemo'));
const GoyaBoutiquePrivate = lazy(() => import('@/pages/GoyaBoutiquePrivate'));
const MhondoroPrivateLodge = lazy(() => import('@/pages/MhondoroPrivateLodge'));
const EdgeLuxuryVillas = lazy(() => import('@/pages/EdgeLuxuryVillas'));
const StockholmByBoat = lazy(() => import('@/pages/StockholmByBoat'));
const GoGalapagos = lazy(() => import('@/pages/GoGalapagos'));
const AccessAir = lazy(() => import('@/pages/AccessAir'));
const MahlatiniSafari = lazy(() => import('@/pages/MahlatiniSafari'));
const TravelPortugalCarla = lazy(() => import('@/pages/TravelPortugalCarla'));
const AitutakiLagoon = lazy(() => import('@/pages/AitutakiLagoon'));
const EdenRockStBarths = lazy(() => import('@/pages/EdenRockStBarths'));
const KindleRidge = lazy(() => import('@/pages/KindleRidge'));
const DDREGlobal = lazy(() => import('@/pages/DDREGlobal'));
const JelGolfTravel = lazy(() => import('@/pages/JelGolfTravel'));
const LuxuryYachtConcierge = lazy(() => import('@/pages/LuxuryYachtConcierge'));
const UniversalPrivateAppDemo = lazy(() => import('@/pages/UniversalPrivateAppDemo'));
const TheVipDubaiDemo = lazy(() => import('@/pages/TheVipDubaiDemo'));
const FortyFiveDegreesSailing = lazy(() => import('@/pages/FortyFiveDegreesSailing'));
const TerrenasVillasDemo = lazy(() => import('@/pages/TerrenasVillasDemo'));
const ExecuJetPrivateOffice = lazy(() => import('@/pages/ExecuJetPrivateOffice'));
const YYachts = lazy(() => import('@/pages/YYachts'));
const PrivateHouseFlagship = lazy(() => import('@/pages/PrivateHouseFlagship'));
const NisekoBlack = lazy(() => import('@/pages/NisekoBlack'));
const PullNorthYachting = lazy(() => import('@/pages/PullNorthYachting'));
const LalilandMiami = lazy(() => import('@/pages/LalilandMiami'));
const Kwandwe = lazy(() => import('@/pages/Kwandwe'));
const AfricanSafariConservation = lazy(() => import('@/pages/AfricanSafariConservation'));
const Catamaran360 = lazy(() => import('@/pages/Catamaran360'));

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

function DemoExitMount() {
  const { pathname } = useLocation();
  if (!pathname.startsWith('/demo')) return null;
  return <DemoExitCTA />;
}

function WorkStudyRoute() {
  const { slug } = useParams();
  return <WorkStudy slug={slug} />;
}

function ValueArticleRoute() {
  const { slug } = useParams();
  return <ValueArticle slug={slug} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClientInstance}>
      <Router>
        <NavigationTracker />
        <ConsentBanner />
        <Suspense fallback={<div className="min-h-screen bg-[#F8F4EC]" />}>
        <Routes>
          <Route path="/" element={
            <LayoutWrapper currentPageName={mainPageKey}>
              <MainPage />
            </LayoutWrapper>
          } />
          {Object.entries(Pages).map(([path, Page]) => (
            <Route
              key={path}
              path={`/${path}`}
              element={
                <LayoutWrapper currentPageName={path}>
                  <Page />
                </LayoutWrapper>
              }
            />
          ))}
          {/* SEO & Content Pages */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/frequently-asked-questions" element={<FAQ />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/scorecard" element={<AuditPage />} />

          {/* The offer: one engagement, two halves, plus proof and the secondary offer */}
          <Route path="/website-redesign" element={<WebsiteRedesign />} />
          <Route path="/the-new-era-of-search" element={<NewEraOfSearch />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<WorkStudyRoute />} />
          <Route path="/client-experience" element={<ClientExperience />} />
          <Route path="/systems" element={<Navigate to="/client-experience" replace />} />
          <Route path="/ai-systems" element={<Navigate to="/client-experience" replace />} />
          <Route path="/client-experience-layer" element={<Navigate to="/client-experience" replace />} />
          {/* likely-typed and legacy aliases */}
          <Route path="/redesign" element={<Navigate to="/website-redesign" replace />} />
          <Route path="/new-era-of-search" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/ai-search" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/ai-search-visibility" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/answer-engine-optimisation" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/generative-engine-optimisation" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/blog/the-new-era-of-search" element={<Navigate to="/the-new-era-of-search" replace />} />
          <Route path="/blog/answer-engine-optimisation" element={<Navigate to="/blog/getting-found-in-ai-answers" replace />} />
          <Route path="/blog/website-redesign-ai-search" element={<Navigate to="/blog/redesigning-for-the-new-search" replace />} />
          <Route path="/services" element={<Navigate to="/website-redesign" replace />} />

          {/* Luxury-operator client-experience vertical pages */}
          <Route path="/yacht-charter-client-portal" element={<VerticalLanding slug="yacht-charter-client-portal" />} />
          <Route path="/villa-rental-guest-portal" element={<VerticalLanding slug="villa-rental-guest-portal" />} />
          <Route path="/dmc-client-portal" element={<VerticalLanding slug="dmc-client-portal" />} />
          <Route path="/private-members-club-portal" element={<VerticalLanding slug="private-members-club-portal" />} />
          <Route path="/luxury-travel-client-portal" element={<VerticalLanding slug="luxury-travel-client-portal" />} />

          {/* The Experience Gap — concept / GEO cornerstone */}
          <Route path="/the-experience-gap" element={<TheExperienceGap />} />

          {/* The private client experience layer — positioning cornerstone */}
          <Route path="/private-client-experience-layer" element={<PrivateClientExperienceLayer />} />
          <Route path="/booking-desk-prototype" element={<AIBookingDeskPrototype />} />

          {/* Sector narrative pages — public fluency, no UI. The interactive demo
              worlds still live at /worlds/<slug> for private sends (unlinked). */}
          <Route path="/charter" element={<Charter />} />
          <Route path="/yachting" element={<Navigate to="/charter" replace />} />
          <Route path="/residences" element={<Sector slug="residences" />} />
          <Route path="/villas" element={<Navigate to="/residences" replace />} />
          <Route path="/travel" element={<Sector slug="travel" />} />
          <Route path="/expeditions" element={<Navigate to="/travel" replace />} />

          {/* Worlds We Design — illustrative private client experience builds */}
          <Route path="/worlds" element={<WorldsWeDesign />} />
          <Route path="/worlds/yacht-charter" element={<YachtCharterWorld />} />
          <Route path="/worlds/private-residences" element={<PrivateResidencesWorld />} />
          <Route path="/worlds/travel-expeditions" element={<TravelExpeditionsWorld />} />
          {/* legacy / retired slugs → closest kept world (avoid 404s on old links) */}
          <Route path="/worlds/private-villas" element={<Navigate to="/worlds/private-residences" replace />} />
          <Route path="/worlds/estate-services" element={<Navigate to="/worlds/private-residences" replace />} />
          <Route path="/worlds/luxury-travel" element={<Navigate to="/worlds/travel-expeditions" replace />} />
          <Route path="/worlds/expedition-safari" element={<Navigate to="/worlds/travel-expeditions" replace />} />
          <Route path="/worlds/private-aviation" element={<Navigate to="/worlds/travel-expeditions" replace />} />
          <Route path="/worlds/private-wealth" element={<Navigate to="/worlds" replace />} />
          <Route path="/worlds/members-club" element={<Navigate to="/worlds" replace />} />

          {/* Blog Articles — luxury cornerstones + catch-all for the vertical article data */}
          <Route path="/blog/luxury-travel-concierge-portals" element={<LuxuryTravelConciergePortals />} />
          <Route path="/blog/private-offices-for-premium-brands" element={<PrivateOfficesForPremiumBrands />} />
          <Route path="/blog/:slug" element={<ValueArticleRoute />} />

          {/* Demo portal pages for sales calls */}
          <Route path="/demo/bhrn" element={<DemoBHRN />} />
          <Route path="/demo/sienna-charles" element={<SiennaPrivateRoom />} />
          <Route path="/demo/tjb-superyachts" element={<TJBPrivateOffice />} />
          <Route path="/demo/vogue-events" element={<VoguePrivateOffice />} />
          <Route path="/demo/brow-factor" element={<BrowFactorDemo />} />
          <Route path="/demo/karma-events" element={<KarmaPrivateSuite />} />
          <Route path="/demo/madison-house" element={<MadisonHousePrivate />} />
          <Route path="/demo/citi-travel-dmc" element={<CitiTravelDMC />} />
          <Route path="/demo/hedonist-experience" element={<HedonistExperience />} />
          <Route path="/demo/plan-japan" element={<PlanJapanPrivateOffice />} />
          <Route path="/demo/cosmetic-courses" element={<CosmeticCoursesDemo />} />
          <Route path="/demo/gina-connor-travel" element={<GinaConnorTravelDemo />} />
          <Route path="/demo/bendita-deco" element={<BenditaDecoPrivate />} />
          <Route path="/demo/yugawara-fukiya" element={<YugawaraFukiyaDemo />} />
          <Route path="/demo/goya-boutique" element={<GoyaBoutiquePrivate />} />
          <Route path="/demo/mhondoro" element={<MhondoroPrivateLodge />} />
          <Route path="/demo/edge-luxury-villas" element={<EdgeLuxuryVillas />} />
          <Route path="/demo/stockholm-by-boat" element={<StockholmByBoat />} />
          <Route path="/demo/go-galapagos" element={<GoGalapagos />} />
          <Route path="/demo/ddre-global" element={<DDREGlobal />} />
          <Route path="/demo/jel-golf-travel" element={<JelGolfTravel />} />
          <Route path="/demo/luxury-yacht-concierge" element={<LuxuryYachtConcierge />} />
          <Route path="/demo/access-air" element={<AccessAir />} />
          <Route path="/demo/mahlatini" element={<MahlatiniSafari />} />
          <Route path="/demo/travel-portugal-carla" element={<TravelPortugalCarla />} />
          <Route path="/demo/aitutaki-lagoon" element={<AitutakiLagoon />} />
          <Route path="/demo/eden-rock" element={<EdenRockStBarths />} />
          <Route path="/demo/kindle-ridge" element={<KindleRidge />} />
          <Route path="/demo/private-client-app" element={<UniversalPrivateAppDemo />} />
          <Route path="/demo/the-vip-dubai" element={<TheVipDubaiDemo />} />
          <Route path="/demo/45-degrees-sailing" element={<FortyFiveDegreesSailing />} />
          <Route path="/demo/terrenas-villas" element={<TerrenasVillasDemo />} />
          <Route path="/demo/execujet" element={<ExecuJetPrivateOffice />} />
          <Route path="/demo/y-yachts" element={<YYachts />} />
          <Route path="/demo/private-house" element={<PrivateHouseFlagship />} />
          <Route path="/demo/laliland" element={<LalilandMiami />} />
          <Route path="/demo/kwandwe" element={<Kwandwe />} />
          <Route path="/demo/african-safari-conservation" element={<AfricanSafariConservation />} />
          <Route path="/demo/pull-north-yachting" element={<PullNorthYachting />} />
          <Route path="/demo/niseko-black" element={<NisekoBlack />} />
          <Route path="/demo/catamaran-360" element={<Catamaran360 />} />

          {/* Direct mail personalized preview pages — noindex */}
          <Route path="/preview/:slug" element={<Preview />} />

          <Route path="*" element={<PageNotFound />} />
        </Routes>
        </Suspense>
      </Router>
      <Toaster />
    </QueryClientProvider>
  )
}

export default App
